<template>
  <modal
    elem-id="modal_cliente_archive"
    :show="isVisible"
    :title="t('Arquivar cliente')"
    :close-label="t('Fechar')"
    :save-label="t('Arquivar cliente')"
    :save-fn="handleArchive"
    :save-disabled-fn="() => !obsArquivado.trim() || isArchiving"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <box type="warning" :title="t('Observação obrigatória')">
      <p>{{ t('Você deve fornecer um motivo para realizar o arquivamento do cliente.') }}</p>
      
      <input-rich-text
        elem-id="cliente_obs_arquivado"
        :show-toolbar="true"
        v-model="obsArquivado"
        :placeholder="t('Observação obrigatória')"
        :description="t('Você deve fornecer um motivo para realizar o arquivamento do cliente.')"
        data-testid="cliente-archive-obs-input">
      </input-rich-text>
    </box>
    
      <div v-if="isArchiving" class="text-center" style="margin-top: 15px;">
        <ProgressSpinner style="width: 1rem; height: 1rem" strokeWidth="4" /> {{ t('Arquivando cliente...') }}
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useCliente } from '../../composables/useCliente';
import { useOcorrencias } from '../../composables/useOcorrencias';

const emit = defineEmits<{
  close: [];
  'archive-success': [];
}>();

const { t } = useI18n();
const { updateCliente } = useCliente();
const { saveOcorrencia } = useOcorrencias();

type ClienteId = number | string;

const isVisible = ref(false);
const clienteId = ref<ClienteId>(0);
const obsArquivado = ref('');
const isArchiving = ref(false);

const open = (id: ClienteId): void => {
  log('ClienteArchiveModal', 'open', 'Opening archive modal for client', id);
  clienteId.value = id;
  obsArquivado.value = '';
  isVisible.value = true;
};

const close = (): void => {
  log('ClienteArchiveModal', 'close', 'Closing archive modal');
  isVisible.value = false;
  clienteId.value = 0;
  obsArquivado.value = '';
  emit('close');
};

const handleClose = (): void => {
  close();
};

interface ModalOpenPayload { id?: ClienteId }

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('ClienteArchiveModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data?.id) {
    open(data.id);
  }
};

const handleArchive = async (): Promise<void> => {
  log('ClienteArchiveModal', 'handleArchive', 'Archiving client', clienteId.value);

  if (!obsArquivado.value.trim()) {
    eventBus.emit('globalMessage.error', t('Você deve fornecer uma observação para arquivar o cliente'));
    return;
  }

  isArchiving.value = true;

  try {
    await saveOcorrencia({
      owner: 'cliente',
      ownerID: Number(clienteId.value),
      content: obsArquivado.value
    });

    await updateCliente({
      cliente_id: clienteId.value,
      cliente_status: '2'
    });

    eventBus.emit('globalMessage.success', t('Cliente arquivado com sucesso'));
    emit('archive-success');
    close();
  } catch (err) {
    log('ClienteArchiveModal', 'handleArchive', 'Error archiving client', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao arquivar cliente: ') + message);
  } finally {
    isArchiving.value = false;
  }
};

onMounted(() => {
  log('ClienteArchiveModal', 'onMounted', 'Component mounted, setting up event listeners');

  eventBus.on('modal.cliente-archive.open', handleModalOpen);
  eventBus.on('modal.cliente-archive.close', close);
});

onBeforeUnmount(() => {
  log('ClienteArchiveModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  eventBus.off('modal.cliente-archive.open', handleModalOpen);
  eventBus.off('modal.cliente-archive.close', close);
});

defineExpose({
  open,
  close
});
</script>

